import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { motion } from 'framer-motion';
import { Shield, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  if (user && user.role === 'admin') {
    navigate('/admin');
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.role === 'admin') {
        navigate('/admin');
      } else {
        setError('Admin access required');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail == null) setError('Something went wrong');
      else if (typeof detail === 'string') setError(detail);
      else if (Array.isArray(detail)) setError(detail.map(e => e.msg || JSON.stringify(e)).join(' '));
      else setError(String(detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="login-page" className="pt-16 md:pt-20 min-h-screen flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#FF2A2A]/10 flex items-center justify-center mx-auto mb-4">
            <Shield size={32} className="text-[#FF2A2A]" />
          </div>
          <h1 className="font-heading text-4xl uppercase tracking-tight">ADMIN LOGIN</h1>
          <p className="font-body text-sm text-[#71717A] mt-2">Manage your podcast episodes</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-[#141414] border border-white/5 p-8 space-y-6" data-testid="login-form">
          {error && (
            <div data-testid="login-error" className="bg-[#FF2A2A]/10 border border-[#FF2A2A]/30 p-3 text-center">
              <p className="font-body text-sm text-[#FF2A2A]">{error}</p>
            </div>
          )}

          <div className="space-y-2">
            <Label className="font-body text-sm font-semibold text-[#A1A1AA] uppercase tracking-wider">Email</Label>
            <Input
              data-testid="login-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@letsgetintoit.com"
              required
              className="bg-[#0A0A0A] border-white/10 text-white h-12 rounded-none focus:border-[#FF2A2A] focus:ring-1 focus:ring-[#FF2A2A]"
            />
          </div>

          <div className="space-y-2">
            <Label className="font-body text-sm font-semibold text-[#A1A1AA] uppercase tracking-wider">Password</Label>
            <Input
              data-testid="login-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              required
              className="bg-[#0A0A0A] border-white/10 text-white h-12 rounded-none focus:border-[#FF2A2A] focus:ring-1 focus:ring-[#FF2A2A]"
            />
          </div>

          <Button
            type="submit"
            data-testid="login-submit-btn"
            disabled={loading}
            className="w-full bg-[#FF2A2A] text-white font-body font-bold uppercase tracking-wide h-12 rounded-none hover:bg-[#E60000] transition-all disabled:opacity-50"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <Loader2 size={18} className="animate-spin" /> Signing in...
              </span>
            ) : (
              'SIGN IN'
            )}
          </Button>
        </form>
      </motion.div>
    </main>
  );
}
